import React, { useState, useMemo, useEffect } from "react";
import { Empty,Drawer, Form, Select, Input, Button as AntButton } from "antd";
import { Col, Row, Spinner, Dropdown } from "react-bootstrap";
import { ExpandOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// Layout & Table Components
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";

// Travel Task Specifics
import useTravelTaskCategories from "hooks/airline/useTravelTaskCategories";

// ================= KEEPING ORIGINAL SUB-COMPONENTS UNTOUCHED =================
const statusStyle = (active) =>
  active
    ? { bg: "#E6F9F0", text: "#059669", label: "Active" }
    : { bg: "#FDECEC", text: "#DC2626", label: "Inactive" };

const DetailRow = ({ label, value, isLast = false }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "170px 1fr",
      gap: 10,
      paddingBottom: isLast ? 0 : 10,
      marginBottom: isLast ? 0 : 10,
      borderBottom: isLast ? "none" : "1px solid #f1f3f5",
      alignItems: "start",
    }}
  >
    <span className="text-muted" style={{ fontSize: 12 }}>{label}</span>
    <span style={{ fontSize: 13, color: "#1f2937", wordBreak: "break-word" }}>{value || "—"}</span>
  </div>
);

const CategoryDetailsDrawer = ({ visible, category, onClose, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => { if (!visible) setIsExpanded(false); }, [visible]);
  const ui = statusStyle(category?.active === true);
  return (
    <Drawer open={visible} onClose={onClose} placement="right" size={isExpanded ? "large" : 600} zIndex={2000} title={null} closable={false} destroyOnHidden>
      <div style={{ maxWidth: isExpanded ? "100%" : 700, margin: isExpanded ? 0 : "0 auto", fontFamily: "Inter, sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span>Airline Management</span><span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>{category?.name || "Category Details"}</span>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            <ExpandOutlined style={{ cursor: "pointer" }} onClick={() => setIsExpanded((prev) => !prev)} />
            <EditOutlined style={{ cursor: "pointer" }} onClick={() => category?.id && onEdit?.(category)} />
          </div>
        </div>
        <h5 className="mb-3">{category?.name || "Category Details"}</h5>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium" style={{ backgroundColor: ui.bg, color: ui.text, fontSize: "0.75rem" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: ui.text }} />{ui.label}
          </span>
          <span className="d-inline-flex align-items-center px-2 py-1 rounded-pill fw-medium" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontSize: "0.75rem" }}>
            {category?.serviceType || "AIRPORT"}
          </span>
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.03em" }}>Category Details</div>
          <DetailRow label="Name" value={category?.name} />
          <DetailRow label="Description" value={category?.description} />
          <DetailRow label="Service Type" value={category?.serviceType || "AIRPORT"} />
          <DetailRow label="ID" value={category?.id} isLast />
        </div>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, background: "#fafafa", padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.03em" }}>Audit</div>
          <DetailRow label="Created By" value={category?.createdBy} />
          <DetailRow label="Created At" value={category?.createdAt ? dayjs(category.createdAt).format("MMM D, YYYY HH:mm") : "—"} />
          <DetailRow label="Updated By" value={category?.updatedBy} />
          <DetailRow label="Updated At" value={category?.updatedAt ? dayjs(category.updatedAt).format("MMM D, YYYY HH:mm") : "—"} isLast />
        </div>
      </div>
    </Drawer>
  );
};

const CategoryFormDrawer = ({ open, onClose, mode = "create", initialValues = null, loading, onSubmit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const isEditMode = mode === "edit";
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      active: initialValues?.active === false ? "inactive" : "active",
      serviceType: initialValues?.serviceType || "AIRPORT",
    });
  }, [open, initialValues, form]);
  const handleSave = async (values) => {
    setIsSubmitting(true);
    try {
      await onSubmit({ name: values.name.trim(), description: values.description?.trim() || "", active: values.active !== "inactive", serviceType: "AIRPORT" });
      form.resetFields();
      onClose();
    } finally { setIsSubmitting(false); }
  };
  return (
    <Drawer zIndex={2000} placement="right" closable={false} size={isExpanded ? "large" : "600"} onClose={onClose} open={open} destroyOnHidden
      footer={<div style={{ textAlign: "right", maxWidth: isExpanded ? "100%" : 700, margin: isExpanded ? 0 : "0 auto", paddingTop: 10, paddingBottom: 10 }}>
        <AntButton style={{ marginRight: 8 }} onClick={onClose} disabled={isSubmitting}>Cancel</AntButton>
        <AntButton type="primary" onClick={() => form.submit()} loading={isSubmitting || loading}>{isEditMode ? "Save Changes" : "Create Category"}</AntButton>
      </div>}>
      <div style={{ maxWidth: isExpanded ? "100%" : 700, margin: isExpanded ? 0 : "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 15, color: "#6b7280", fontFamily: "Inter" }}>
            <span>Airline Management</span><span style={{ margin: "0 6px" }}>/</span><span style={{ color: "#111827", fontWeight: 500 }}>{isEditMode ? "Edit Travel Category" : "Create Travel Category"}</span>
          </div>
          <ExpandOutlined onClick={() => setIsExpanded((prev) => !prev)} style={{ cursor: "pointer" }} />
        </div>
        <Form layout="vertical" form={form} onFinish={handleSave} initialValues={{ active: "active", serviceType: "AIRPORT" }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Name is required" }]}><Input placeholder="e.g. Other (Custom Luggage Help)" maxLength={120} /></Form.Item>
          <Form.Item label="Description" name="description"><Input.TextArea rows={3} placeholder="Describe category" maxLength={300} /></Form.Item>
          <Form.Item label="Service Type" name="serviceType"><Select disabled options={[{ label: "AIRPORT", value: "AIRPORT" }]} /></Form.Item>
          <Form.Item label="Status" name="active"><Select options={[{ label: "Active", value: "active" }, { label: "Inactive", value: "inactive" }]} /></Form.Item>
        </Form>
      </div>
    </Drawer>
  );
};

// ================= MAIN COMPONENT =================
export default function TravelTaskCategories() {
  const { categories = [], loading, createCategory, updateCategory, deleteCategory } = useTravelTaskCategories();

  // State for Pagination, Search and Filter (Reference Style)
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  // Modal/Drawer States
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showFormDrawer, setShowFormDrawer] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Original Formatter
  const formatText = (text) => (text ? `${text.slice(0, 14)}...` : "None");

  // ================= FILTER & PAGINATION LOGIC (Reference Style) =================
  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (categories || []).filter((item) => {
      const statusLabel = item?.active === true ? "ACTIVE" : "INACTIVE";
      const byStatus = selectedStatus === "ALL" || selectedStatus === statusLabel;
      if (!term) return byStatus;
      const haystack = [item?.name, item?.description, item?.serviceType || "AIRPORT", statusLabel]
        .filter(Boolean).join(" ").toLowerCase();
      return byStatus && haystack.includes(term);
    });
  }, [categories, searchTerm, selectedStatus]);

  const totalElements = filteredCategories.length;
  const totalPages = totalElements > 0 ? Math.ceil(totalElements / pageSize) : 0;

  const paginatedData = useMemo(() => {
    const start = page * pageSize;
    return filteredCategories.slice(start, start + pageSize);
  }, [filteredCategories, page, pageSize]);

  // ================= COLUMNS (Keeping Original Logic) =================
  const columns = useMemo(() => [
    { accessorKey: "name", header: "Name", meta: { headerProps: { className: "text-900 fw-bold" } } },
    { accessorKey: "description", header: "Description", cell: ({ getValue }) => formatText(getValue()), meta: { headerProps: { className: "text-900 fw-bold" } } },
    { accessorKey: "serviceType", header: "Service Type", cell: ({ getValue }) => getValue() || "AIRPORT", meta: { headerProps: { className: "text-900 fw-bold" } } },
    {
      accessorKey: "active",
      header: "Status",
      meta: { 
        headerProps: { 
          className: "text-center text-900 fw-bold",
          style: { width: '100px' } 
        },
        cellProps: { className: "text-center" } 
      },
      cell: ({ getValue }) => {
        const isActive = getValue();
        const style = {
          backgroundColor: isActive ? "#e8f7e3" : "#fbe9e7",
          color: isActive ? "#2e7d32" : "#c62828",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          lineHeight: 1.2,
          fontWeight: 600,
          border: isActive ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
          display: "inline-block",
          textTransform: "uppercase"
        };
        return <span style={style}>{isActive ? "ACTIVE" : "INACTIVE"}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      meta: { headerProps: { className: "text-center text-900 fw-bold", style: { width: '80px' } }, cellProps: { className: "text-center" } },
      cell: ({ row }) => (
        <CardDropdown drop="start">
          <div className="py-2">
            <Dropdown.Item onClick={() => { setSelectedCategory(row.original); setShowDetailsDrawer(true); }}>View</Dropdown.Item>
            <Dropdown.Item onClick={() => { setFormMode("edit"); setSelectedCategory(row.original); setShowFormDrawer(true); }}>Edit</Dropdown.Item>
            <Dropdown.Divider as="div" />
            <Dropdown.Item className="text-danger" onClick={() => { setCategoryToDelete(row.original); setShowDeleteModal(true); }}>Delete</Dropdown.Item>
          </div>
        </CardDropdown>
      ),
    },
  ], []);

  const table = useAdvanceTable({ data: paginatedData, columns, sortable: true, pagination: false });

  return (
    <>
      <WidgetSectionTitle icon="suitcase" title="TravoTask Categories" subtitle="Manage airport TravoTask categories used by airline operations." className="mb-3 mt-3" />

      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <IconButton
            variant="falcon-default" size="sm" icon="plus"
            onClick={() => { setFormMode("create"); setSelectedCategory(null); setShowFormDrawer(true); }}
            style={{ backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981", fontWeight: 600 }}
          >
            New Travel Category
          </IconButton>
        </Col>
      </Row>

      {/* ================= SEARCH & FILTER ROW (Reference Style) ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search by name, code or affiliation..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              allowClear
              size="large"
              style={{ borderRadius: 12, height: 30, flex: "1 1 300px", maxWidth: 300 }}
            />
            <Select
              size="large"
              value={selectedStatus}
              onChange={(val) => { setSelectedStatus(val); setPage(0); }}
              options={[
                { label: "🌍 ALL STATUS", value: "ALL" },
                { label: "✅ ACTIVE", value: "ACTIVE" },
                { label: "🚫 INACTIVE", value: "INACTIVE" },
              ]}
              style={{ minWidth: 180, height: 28 }}
            />
          </div>
        </Col>
        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{totalElements}</span> of <span className="fw-semibold text-dark">{categories.length}</span>
          </div>
        </Col>
      </Row>

      <AdvanceTableProvider {...table}>
        {loading && totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner animation="border" variant="success" />
          </div>
        ) : totalElements === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Empty description="No travel categories found" />
          </div>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-light text-nowrap align-middle text-dark"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0 overflow-hidden" }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={page > 0 ? () => setPage(page - 1) : undefined}
                onNext={page < totalPages - 1 ? () => setPage(page + 1) : undefined}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      {/* Keep Drawers and Modal logic exactly as they were */}
      <CategoryFormDrawer open={showFormDrawer} onClose={() => setShowFormDrawer(false)} mode={formMode} initialValues={selectedCategory} loading={loading} onSubmit={(payload) => formMode === "edit" ? updateCategory(selectedCategory.id, payload) : createCategory(payload)} />
      <CategoryDetailsDrawer visible={showDetailsDrawer} category={selectedCategory} onClose={() => setShowDetailsDrawer(false)} onEdit={(item) => { setShowDetailsDrawer(false); setFormMode("edit"); setSelectedCategory(item); setShowFormDrawer(true); }} />
      <ConfirmDeleteModal show={showDeleteModal} setShow={setShowDeleteModal} itemName="travel category" loading={loading} onConfirm={() => deleteCategory(categoryToDelete?.id)} />
    </>
  );
}